import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { Step, SlotReservation, MscSite, BookingCustomer, Household, CreateBookingResult } from "./publicBookingFlowTypes";

export interface TerminalState {
  type: string;
  detail?: Record<string, any>;
}

export interface PublicBookingFlowState {
  currentStep: Step;
  selectedZipCode: string;
  selectedSite: MscSite | null;
  targetSlot: SlotReservation | null;
  customerData: BookingCustomer | null;
  /**
   * The booking programs ("Pse", "Liheap") the step-2 program check found the household already
   * received this program year (AB#590). Empty when the check did not apply, failed, or no SSN was
   * given. Advisory for the page only: the server keeps its own copy and refuses on its own answer.
   */
  receivedPrograms: string[];
  householdData: Household | null;
  confirmedBooking: CreateBookingResult | null;
  terminal: TerminalState | null;
}

const initialState: PublicBookingFlowState = {
  currentStep: 1,
  selectedZipCode: "",
  selectedSite: null,
  targetSlot: null,
  customerData: null,
  receivedPrograms: [],
  householdData: null,
  confirmedBooking: null,
  terminal: null,
};

const publicBookingFlowSlice = createSlice({
  name: "publicBookings",
  initialState,
  reducers: {
    flowRestarted() {
      return initialState;
    },
    stepChanged(state: PublicBookingFlowState, action: PayloadAction<Step>) {
      state.currentStep = action.payload;
    },
    selectedZipCodeChanged(state: PublicBookingFlowState, action: PayloadAction<string>) {
      state.selectedZipCode = action.payload;
    },
    siteSelected(state: PublicBookingFlowState, action: PayloadAction<MscSite>) {
      state.selectedSite = action.payload;
    },
    slotReserved(state: PublicBookingFlowState, action: PayloadAction<SlotReservation>) {
      state.targetSlot = action.payload;
    },
    householdDataSaved(state: PublicBookingFlowState, action: PayloadAction<Household>) {
      state.householdData = action.payload;
    },
    receivedProgramsChanged(state: PublicBookingFlowState, action: PayloadAction<string[]>) {
      state.receivedPrograms = action.payload;
    },
    customerDataSaved(state: PublicBookingFlowState, action: PayloadAction<BookingCustomer>) {
      state.customerData = action.payload;
    },
    bookingConfirmed(state: PublicBookingFlowState, action: PayloadAction<CreateBookingResult>) {
      state.confirmedBooking = action.payload;
    },
    reachedTerminal(state: PublicBookingFlowState, action: PayloadAction<TerminalState>) {
      state.terminal = action.payload;
    },
    // "Find another time": a new hold for the saved ZIP replaces the one that is gone, and the flow
    // goes back to the booking step, which submits on mount against this hold. The applicant's
    // answers (customerData, householdData) and the saved ZIP are deliberately left as they are.
    // The hold is the server's idempotency key, so a new hold is a new key: the old one is replaced,
    // never resubmitted.
    reservationRecovered(state: PublicBookingFlowState, action: PayloadAction<SlotReservation>) {
      state.targetSlot = action.payload;
      state.selectedSite = action.payload.site;
      state.terminal = null;
      state.currentStep = 5;
    }
  },
});

export const {
  flowRestarted,
  stepChanged,
  selectedZipCodeChanged,
  siteSelected,
  slotReserved,
  householdDataSaved,
  receivedProgramsChanged,
  customerDataSaved,
  bookingConfirmed,
  reachedTerminal,
  reservationRecovered
} =publicBookingFlowSlice.actions;

/** Whether the household already received PSE HELP this program year, from the step-2 program check. */
export const selectPseHelpAlreadyAssigned = (state: { publicBookingFlow: PublicBookingFlowState }): boolean =>
  state.publicBookingFlow.receivedPrograms.includes("Pse");

export default publicBookingFlowSlice.reducer;
